import React, { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import * as Yup from "yup";
import { useFormik } from "formik";
import TextInput from "../../common/textfield/TextInput";
import SelectFiled from "../../common/textfield/SelectFiled";
import { Button } from "../../common/Button";
import MultiSelect from "../../common/textfield/MultiSelect";
import moment from "moment";
import { createContactNumber } from "../../../utils/phoneNumberSupport";
import Fields from "../Individuals/Fields";
import {
  createBasicFieldsPractionerTwo,
  FormikObjCreate,
} from "../Individuals/Constants";
import { caregions, countryList, usregions } from "../../../utils/constants";
// import SignatureCapture from "../../Profile/SignatureCapture";
import { useLocation, useNavigate } from "react-router-dom";
import { SAMLLogin } from "../../api/IDP";
import { failed, success } from "../../common/Toastify";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";

function AccountSetup() {
  const location = useLocation();
  const data = location.state?.data;
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  
  // Security check: Ensure user has valid SAML data
  useEffect(() => {
    if (!data || !data.email || !data.access_token || !data.id_token) {
      failed("Invalid access. Please contact your administrator.");
      navigate("/login");
      return;
    }
    setIsValidating(false);
  }, [data, navigate]);
  
  const availableRoles = data?.availableRoles || [];
  const organizationData = data?.managingOrganization;
  const email = data?.email;
  const access_token = data?.access_token;
  const id_token = data?.id_token;
  const settings = data?.orgSettings;

  const [btnLoading, setBtnLoading] = useState(false);
  const [regionOptions, setRegionOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [enforcePractitionerDOB, setEnforcePractitionerDOB] = useState(false);
  const [enforcePractitionerGender, setEnforcePractitionerGender] = useState(false);
  const [enforcePractitionerAddress, setEnforcePractitionerAddress] = useState(false);
  const [enforcePractitionerCountry, setEnforcePractitionerCountry] = useState(false);
  const [enforcePractitionerProvince, setEnforcePractitionerProvince] = useState(false);
  const [enforcePractitionerCity, setEnforcePractitionerCity] = useState(false);
  const [enforcePractitionerPostal, setEnforcePractitionerPostal] = useState(false);

  if (data?.address?.state && data?.address?.state.indexOf("-") >= 0 && !data?.address?.country) {
    data.address.country = data?.address?.state.split("-")[0];
    data.address.state = data?.address?.state.split("-")[1];
  }

  useEffect(() => {
    const p = settings?.practitioner || {};
    setEnforcePractitionerDOB(!!p.dob);
    setEnforcePractitionerGender(!!p.gender);
    setEnforcePractitionerAddress(!!p.address);
    setEnforcePractitionerCountry(!!p.country);
    setEnforcePractitionerProvince(!!p.province);
    setEnforcePractitionerCity(!!p.city);
    setEnforcePractitionerPostal(!!p.postalCode);
  }, [settings]);

  const formik = useFormik({
    ...FormikObjCreate,
    initialValues: {
      ...FormikObjCreate.initialValues,
      type: "Practitioner",
      first_name: data?.name?.given_name || "",
      last_name: data?.name?.family_name || "",
      email: email || "",
      gender: data?.gender?.toLowerCase() || "",
      date_of_birth: data?.birthDate ? moment(data?.birthDate).format("MM/DD/YYYY") : "",
      organization: organizationData?.name || "",
      address: data?.address?.line?.[0] || "",
      city: data?.address?.city || "",
      country: data?.address?.country || "",
      region: data?.address?.state || "",
      postalCode: data?.address?.postalCode || "",
      countrylist: "",
      jurisdiction: [],
      agentId: data?.agentId || "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required first name field")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
      last_name: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required last name field")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid."),

      qualification: Yup.string().max(50, "Must be 50 characters or less").matches(
        /^$|^\S+.*/,
        "Only blankspaces is not valid."
      ),
      license: Yup.string().max(20, "Must be 20 characters or less").matches(
        /^$|^\S+.*/,
        "Only blankspaces is not valid."
      ),

      date_of_birth: enforcePractitionerDOB
        ? Yup.string()
            .required("Required date of birth field")
            .test("min-age", "Age cannot be below 16 years. Please provide a valid age.", (value) => {
              if (!value) return false;
              const birthDate = moment(value, "MM/DD/YYYY", true);
              if (!birthDate.isValid()) return false;
              let minDate = moment().subtract(125, "years");
              if (birthDate.isBefore(minDate)) {
                return this.createError({
                    message: "Date of Birth cannot be more than 125 years ago.",
                });
              }
              return moment().diff(birthDate, "years") >= 16;
            })
        : Yup.string().test("min-age", "Age cannot be below 16 years. Please provide a valid age.", (value) => {
            if (!value) return true;
            const birthDate = moment(value, "MM/DD/YYYY", true);
            if (!birthDate.isValid()) return false;
            return moment().diff(birthDate, "years") >= 16;
          }),

      phone_number: Yup.string().matches(
        /^(?:\+?1[-. ]?)?\(?[0-9]\d{2}\)?[-. ]?\d{3}[-. ]?\d{4}$/,
        "Enter Valid Phone Number."
      ),
      home_phone_number: Yup.string().matches(
        /^(?:\+?1[-. ]?)?\(?[0-9]\d{2}\)?[-. ]?\d{3}[-. ]?\d{4}$/,
        "Enter Valid Home Phone Number."
      ),
      ext: Yup.string().matches(/^\d{0,10}$/, "Must be 10 digits or less."),

      address: enforcePractitionerAddress
        ? Yup.string().required("Required address field").matches(/^$|^\S+.*/, "Only blankspaces is not valid.")
        : Yup.string().matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
      city: enforcePractitionerCity
        ? Yup.string().required("Required city field").matches(/^$|^\S+.*/, "Only blankspaces is not valid.")
        : Yup.string().matches(/^$|^\S+.*/, "Only blankspaces is not valid."),

      email: Yup.string().email("Invalid email address").required("Required email field"),

      organization: Yup.string().required("Required organization").matches(
        /^$|^\S+.*/,
        "Only blankspaces is not valid."
      ),

      gender: enforcePractitionerGender
        ? Yup.string().required("Required gender field").matches(/^$|^\S+.*/, "Only blankspaces is not valid.")
        : Yup.string().matches(/^$|^\S+.*/, "Only blankspaces is not valid."),

      country: enforcePractitionerCountry
        ? Yup.string().required("Required country field").matches(/^$|^\S+.*/, "Only blankspaces is not valid.")
        : Yup.string().matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
      region: enforcePractitionerProvince
        ? Yup.string().required("Required field").matches(/^$|^\S+.*/, "Only blankspaces is not valid.")
        : Yup.string().matches(/^$|^\S+.*/, "Only blankspaces is not valid."),

      postalCode: Yup.string().when(["country"], (country, schema) => {
        const ca = Yup.string().matches(
          /^[A-Za-z]\d[A-Za-z] [0-9][A-Za-z]\d$/,
          "Postal code must be in A1A 1A1 format."
        );
        const us = Yup.number().typeError("Zip code must be a number");

        if (enforcePractitionerPostal) {
          if (country === "US") return us.required("Required field");
          if (country === "CA") return ca.required("Required field");
          return Yup.string().required("Required field");
        } else {
          if (country === "US") return us;
          if (country === "CA") return ca;
          return Yup.string();
        }
      }),


      countrylist: Yup.string().required("Select a jurisdiction country"),
      jurisdiction: Yup.array().min(1, "Select at least one jurisdiction").required(),
    }),
    onSubmit: (values) => {
      setIsLoading(true);
      setBtnLoading(true);

      const phoneNumber = createContactNumber(values);

      const chosen = availableRoles.find((r) => r?.name === values.role);

      const stateForAddress =
        values?.country ? `${values.country}-${values.region}` : values?.region;
      const countryForAddress =
        !values?.country && values?.region?.includes("-")
          ? values?.region.split("-")[0]
          : values?.country;

      const payload = {
        name: [
          {
            family: values?.last_name?.toLowerCase(),
            given: [values?.first_name?.toLowerCase()],
            text: `${values?.first_name?.toLowerCase()} ${values?.last_name?.toLowerCase()}`,
            use: "usual",
          },
        ],
        address: [
          {
            city: values?.city,
            country: countryForAddress,
            line: [values?.address],
            postalCode: values?.postalCode?.toUpperCase(),
            state: stateForAddress,
            use: "home",
          },
        ],
        birthDate: values?.date_of_birth
          ? moment(values?.date_of_birth, "MM/DD/YYYY").format("MM-DD-YYYY")
          : "",
        phone_number: phoneNumber,
        gender: values?.gender,
        email: (email || "").toLowerCase(),
        organization: [
          {
            display: organizationData?.name,
            reference: `Organization/${organizationData?.id}`,
            type: "Organization",
          },
        ],
        telecom: [
          { system: "phone", use: "work", value: phoneNumber },
          { system: "phone", use: "home", value: values?.home_phone_number || "" },
          { system: "email", use: "work", value: (email || "").toLowerCase() },
        ],
        scopeOfPractice: values?.jurisdiction || [],
        code: {
          coding: [
            {
              id: chosen?.id,
              code: chosen?.code,
              display: chosen?.name,
              system: chosen?.system,
              type: chosen?.type,
            },
          ],
        },
        qualification: [
          {
            code: {
              coding: [{ display: "college", text: values?.qualification }],
            },
            identifier: [
              {
                type: { text: "license" },
                use: "usual",
                value: values?.license,
              },
            ],
          },
        ],
        agentId: values?.agentId || null,
      };

      const APIPayload = {
        profile: payload,
        access_token,
        id_token,
        createAccount: true,
      };

      SAMLLogin(APIPayload)
        .then((res) => {
          if (res?.status === true) {
            navigate("/login");
            success(res?.message);
          } else {
            failed(res?.error || "Failed to set up profile");
          }
        })
        .catch((error) => {
          failed(error?.response?.data?.message || "Error setting up profile");
          console.error("ERROR FROM ACCOUNT SETUP: ", error);
        })
        .finally(() => {
          setIsLoading(false);
          setBtnLoading(false);
        });
    },
  });

  useEffect(() => {
    if (data?.birthDate) {
      formik.setFieldValue(
        "date_of_birth",
        moment(data?.birthDate, ["DD-MM-YYYY", "YYYY-MM-DD", "MM/DD/YYYY"], true).isValid()
          ? moment(data?.birthDate, ["DD-MM-YYYY", "YYYY-MM-DD", "MM/DD/YYYY"]).format("MM/DD/YYYY")
          : ""
      );
    }
  }, [data?.birthDate]);

  const handleFormChange = (e) => {
    if (e?.target?.id === "country") {
      formik.setFieldValue("region", "");
      formik.setFieldValue("postalCode", "");
    }
  };

  const handleJurisdictionCountryChange = (event) => {
    const value = event?.target?.value;
    formik.setFieldValue("countrylist", value);
    if (value === "CA") setRegionOptions(caregions);
    else if (value === "US") setRegionOptions(usregions);
    else setRegionOptions([]);
    formik.setFieldValue("jurisdiction", []);
  };

  const handlePostalCodeChange = (e) => {
    const { value } = e.target;
    let spacedValue = value;
    if (formik?.values?.country !== "US") {
      const formattedValue = value.replace(/[^A-Za-z0-9]/g, "");
      spacedValue = formattedValue.replace(/^([A-Za-z0-9]{3})([A-Za-z0-9]+)/, "$1 $2");
    }
    formik?.setFieldValue("postalCode", spacedValue);
  };

  const handleExt = (event) => {
    const key = event.key;
    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      return false;
    }
  };


  const genderOptions = [
    { value: "male", name: "Male" },
    { value: "female", name: "Female" },
    { value: "other", name: "Other" },
  ];

  const createBasicFields = [
    [
      {
        size: "3",
        fieldType: "TextInput",
        keyField: "first_name",
        label: "First Name",
        placeholder: "First Name",
        required: true,
        hideRequired: false,
      },
      {
        size: "3",
        fieldType: "TextInput",
        keyField: "last_name",
        label: "Last Name",
        placeholder: "Last Name",
        required: true,
        hideRequired: false,
      },
      {
        fieldType: "ReactDatePicker",
        keyField: "date_of_birth",
        label: "Date of Birth",
        placeholder: "Select Date",
        hasValue: "date_of_birth",
        displayAge: true,
        required: enforcePractitionerDOB,
        hideRequired: !enforcePractitionerDOB,
      },
      {
        fieldType: "SelectFiled",
        keyField: "gender",
        label: "Select Gender",
        options: genderOptions,
        required: enforcePractitionerGender,
        hideRequired: !enforcePractitionerGender,
      },
    ],
    [
      {
        size: "12",
        fieldType: "TextInput",
        keyField: "address",
        label: "Address",
        placeholder: "Address",
        required: enforcePractitionerAddress,
        hideRequired: !enforcePractitionerAddress,
      },
    ],
    [
      {
        size: "6",
        fieldType: "CountrySelect",
        keyField: "country",
        label: "Country",
        required: enforcePractitionerCountry,
        hideRequired: !enforcePractitionerCountry,
      },
      {
        size: "6",
        fieldType: "RegionSelect",
        keyField: "region",
        required: enforcePractitionerProvince,
        hideRequired: !enforcePractitionerProvince,
      },
    ],
    [
      {
        size: "6",
        fieldType: "TextInput",
        keyField: "city",
        label: "City",
        placeholder: "City",
        required: enforcePractitionerCity,
        hideRequired: !enforcePractitionerCity,
      },
      {
        size: "6",
        fieldType: "postalCode",
        required: enforcePractitionerPostal,
        hideRequired: !enforcePractitionerPostal,
      },
    ],
  ];

  // Don't render content until validation is complete
  if (isValidating) {
    return <FullPageSpinner loadingText="Validating access..." />;
  }

  return (
    <section className="common-listing">
      {isLoading && <FullPageSpinner loadingText="Loading..." />}
      <div className="custom-card p-4">
        <form
          className="common-form border-fields userAccount"
          onSubmit={formik.handleSubmit}
          onChange={handleFormChange}
        >
          {/* Main practitioner demographic & address block */}
          <Fields
            fields={createBasicFields}
            formik={formik}
            isReadOnly={false}
            handlePostalCodeChange={handlePostalCodeChange}
          />

          {/* SAML-locked org & email */}
          <Row>
            <Col sm lg="4">
              <TextInput
                keyField={"organization"}
                label={"Organization"}
                formik={formik}
                placeholder={"Organization"}
                required={true}
                disabled={true}
              />
            </Col>
            <Col sm lg="4">
              <TextInput
                keyField={"email"}
                label={"Email Address"}
                formik={formik}
                placeholder={"Email Address"}
                required={true}
                disabled={true}
              />
            </Col>
            <Col sm lg="4">
              <TextInput
                keyField={"agentId"}
                label={"Agent ID"}
                formik={formik}
                placeholder={"Agent ID"}
                tooltip={"User identifier from telephony agent (CTI integration)"}
                hideRequired={true}
              />
            </Col>
          </Row>

          {/* Phones */}
          <Row>
            <Col>
              <TextInput
                keyField={"phone_number"}
                type="phone"
                label={"Work Phone Number"}
                formik={formik}
                placeholder={"Work Phone Number"}
                hideRequired={true}
              />
            </Col>

            {formik.values.phone_number && (
              <Col sm lg="2">
                <TextInput
                  keyField={"ext"}
                  label={"Ext."}
                  type="text"
                  maxlength="4"
                  formik={formik}
                  placeholder={"Ext."}
                  onKeyPress={handleExt}
                  autocomplete="off"
                  hideRequired={true}
                />
              </Col>
            )}

            <Col>
              <TextInput
                keyField={"home_phone_number"}
                type="phone"
                label={"Home Phone Number"}
                formik={formik}
                placeholder={"Home Phone Number"}
                hideRequired={true}
              />
            </Col>
          </Row>

          <Fields
            fields={createBasicFieldsPractionerTwo}
            formik={formik}
            isReadOnly={false}
            handlePostalCodeChange={handlePostalCodeChange}
          />

          <Row  style={{ borderTop: "1px solid rgba(0,0,0,0.14)", marginTop: 12, paddingTop: 12 }}>
            <Row style={{ fontWeight: "bold", marginBottom: 15, fontSize: 18 }}>Scope of Practice</Row>
            <Col xs lg="3">
              <SelectFiled
                keyField={"countrylist"}
                label={"Country"}
                formik={formik}
                options={countryList}
                onChange={handleJurisdictionCountryChange}
                required={true}
              />
            </Col>
            <Col>
              <MultiSelect
                options={regionOptions}
                keyField={"jurisdiction"}
                formik={formik}
                isSelectAllEnable={true}
                label={"Province/State"}
                required={true}
              />
            </Col>
          </Row>

          {/* Signature (kept as your commented modal) */}
          {/*
          <Row>
            <div>
              <button className="btn-wrap" type="button" onClick={openSignatureModal}>
                Click Here To Set Up Signature
              </button>
            </div>
            {isSignatureModalOpen && (
              <div className="signature-modal">
                <div className="signature-modal-content">
                  <span className="close" onClick={closeSignatureModal}>&times;</span>
                  <SignatureCapture
                    onSignatureCapture={handleSignatureCapture}
                    closeSignatureModal={closeSignatureModal}
                  />
                </div>
              </div>
            )}
          </Row>
          */}

          <div className="btn-wrap" style={{ display: "flex", gap: "16px" }}>
            <Button onClick={() => navigate(-1)} variant="secondary" title="Cancel">
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default AccountSetup;